import {ServiceBase} from "../ServiceBase";
import {
    AIGamesRecommendations, GetTrendingGamesRecommendationsForPlayerOptions,
    IAiRecommendationsReaderService,
    IGetGamesRecommendationsOptions, TrendingGamesRecommendations
} from "./ai-recommendations-reader.service.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {RegularGamesRecommendationsModel} from "./models/regular-games-recommendations.model";
import {RecentlyAddedGamesRecommendationsModel} from "./models/recently-added-games-recommendations.model";
import {TrendingGamesRecommendationsModel} from "./models/trending-games-recommendations.model";

export class AiRecommendationsReaderService extends ServiceBase implements IAiRecommendationsReaderService {

    constructor(services: IServiceFactoryExtended) {
        super(services);
        this._regularGamesRecommendations = new RegularGamesRecommendationsModel(services);
        this._recentlyAddedGamesRecommendations = new RecentlyAddedGamesRecommendationsModel(services);
        this._trendingGamesRecommendations = new TrendingGamesRecommendationsModel(services);
    }

    private readonly _regularGamesRecommendations: RegularGamesRecommendationsModel;
    private readonly _recentlyAddedGamesRecommendations: RecentlyAddedGamesRecommendationsModel;
    private readonly _trendingGamesRecommendations: TrendingGamesRecommendationsModel

    async getRegularGamesRecommendations(options?: IGetGamesRecommendationsOptions): Promise<AIGamesRecommendations> {
        const recommendations = await this._regularGamesRecommendations.getRecommendations();
        return await this._filterGamesRecommendations(recommendations, options);
    }
    async getRecentlyAddedGamesRecommendations(options?: IGetGamesRecommendationsOptions): Promise<AIGamesRecommendations> {
        const recommendations = await this._recentlyAddedGamesRecommendations.getRecommendations();
        return await this._filterGamesRecommendations(recommendations, options);
    }

    async getTrendingGamesRecommendationsForPlayer(options: GetTrendingGamesRecommendationsForPlayerOptions): Promise<TrendingGamesRecommendations> {
        const recommendations = await this._trendingGamesRecommendations.getRecommendations(options.recommendationTypes);

        for(let recomType of Object.keys(recommendations)) {
            recommendations[recomType] = await this._filterByGamesInLayout(recommendations[recomType]);
        }
        return recommendations;
    }

    private async _filterGamesRecommendations(gamesRecommendations: AIGamesRecommendations, options: IGetGamesRecommendationsOptions | undefined): Promise<AIGamesRecommendations> {
        options = {
            maxGamesCount: 15,
            ...options
        }

        let gameTypes = await this._filterByGamesInLayout(gamesRecommendations.gameTypes);
        gameTypes = await this._filterGamesByRecentlyPlayed(gameTypes, options);
        gameTypes = gameTypes.slice(0, options.maxGamesCount);


        return {
            engine: gamesRecommendations.engine,
            gameTypes: gameTypes
        }
    }

    private async _filterByGamesInLayout(gameTypes: number[]): Promise<number[]> {
        const gamesMap = await this.services.games.getUnrestrictedGamesInfoMap(gameTypes);
        return gameTypes.filter(gt => Boolean(gamesMap[gt]));
    }

    private async _filterGamesByRecentlyPlayed(gameTypes: number[], options: IGetGamesRecommendationsOptions): Promise<number[]> {
        if(!options.shouldFilterByRecentlyPlayed) {
            return gameTypes;
        }

        let recentlyPlayedGameTypes = await this.services.games.getRecentlyPlayedGamesAsync();

        if(recentlyPlayedGameTypes.length === 0) {
            return gameTypes;
        }

        if(options.numberOfRecentlyPlayedGamesToFilterOut) {
            recentlyPlayedGameTypes = recentlyPlayedGameTypes.slice(0, options.numberOfRecentlyPlayedGamesToFilterOut);
        }

        return gameTypes.filter(gt => !recentlyPlayedGameTypes.includes(gt));
    }

}